import {
  Switch,
  Route,
  useHistory,
  useLocation,
  BrowserRouter,
} from 'react-router-dom';
import { ErrorBoundary } from '@hungryroot/component-error-boundary';
import { useLogUser } from '@hungryroot/customer';
import { fullPageRedirect } from '@hungryroot/environment';
import { useLogExperiments } from '@hungryroot/experiments';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { useIsPromoFreeItem } from '@hungryroot/rest-hooks';
import {
  CYO_ROUTE,
  ROUTE_MINI_QUIZ_BUDGET_BENEFITS,
  ROUTE_QUIZ_BUILDING,
  ROUTE_SIGN_UP,
} from '@hungryroot/routes';

import { markQuizCompleted } from '../../defs/localStorage';
import {
  isMiniQuizTailSession,
  MINI_QUIZ_TAIL_SUPPRESSED_EXPERIMENTS,
} from '../../defs/miniQuizTail';
import { GOOGLE_SHOPPING_PRESET_PLANS_FEATURE_FLAG } from '../../defs/presetPlanOverride';
import { useCapturePresetPlanOverride } from '../../hooks/useCapturePresetPlanOverride';
import { useFunnelRedirects } from '../../hooks/useFunnelRedirects';
import { usePromoCodeFromQueryParamsSessionStorage } from '../../hooks/usePromoCodeFromQueryParamsSessionStorage';
import { Building } from '../Building/Building';
import { MiniQuizTailHeader } from '../MiniQuizTailHeader/MiniQuizTailHeader';
import {
  PlanContainer,
  planContentHeader,
} from '../PlanContainer/PlanContainer';
import { Quiz } from '../Quiz/Quiz';

const WINDOW_LOCATION_REDIRECT_ROUTES = [ROUTE_SIGN_UP, CYO_ROUTE];

interface QuizComponentProps {
  testid?: string;
}

export function QuizComponent({ testid }: QuizComponentProps) {
  const history = useHistory();
  const { pathname } = useLocation();
  const isTail = isMiniQuizTailSession();
  const funnelRedirects = useFunnelRedirects(
    isTail
      ? { suppressEnrollment: MINI_QUIZ_TAIL_SUPPRESSED_EXPERIMENTS }
      : undefined
  );

  useLogUser();
  useLogExperiments();
  const isPresetPlanOverrideEnabled =
    useFeatureFlag(GOOGLE_SHOPPING_PRESET_PLANS_FEATURE_FLAG) === true;
  useCapturePresetPlanOverride({ enabled: isPresetPlanOverrideEnabled });

  // sometimes users are redirected to the quiz with a promo code in the URL params
  // we need to store the promo code in session storage so we can use it in /limited-time
  const promoCode = usePromoCodeFromQueryParamsSessionStorage();
  const { data } = useIsPromoFreeItem(promoCode);

  if (funnelRedirects === null) {
    return null;
  }

  const { afterBuilding, afterQuiz, beforeCyo, beforePlan } = funnelRedirects;

  return (
    <>
      <Switch>
        <Route path="/survey/plan/">
          <PlanContainer
            hideSecondaryNav={isTail}
            onBackButtonClick={() => {
              if (isTail) {
                fullPageRedirect(ROUTE_MINI_QUIZ_BUDGET_BENEFITS);
                return;
              }

              if (
                WINDOW_LOCATION_REDIRECT_ROUTES.includes(beforePlan) &&
                data?.is_free_item
              ) {
                fullPageRedirect(beforeCyo);
                return;
              }

              if (WINDOW_LOCATION_REDIRECT_ROUTES.includes(beforePlan)) {
                fullPageRedirect(beforePlan);
                return;
              }

              history.push(beforePlan);
            }}
            pageHeader={
              isTail ? (
                <MiniQuizTailHeader
                  selected="Your first delivery"
                  pathname={pathname}
                />
              ) : (
                planContentHeader
              )
            }
          />
        </Route>
        <Route path={ROUTE_QUIZ_BUILDING}>
          <Building
            onComplete={(path) => {
              // Anonymous hand-off to signup is the moment the full quiz is
              // complete. Flag it beside the answers; `quiz.nav` is left alone
              // so the resume CTAs keep pointing at the last quiz screen.
              if (path === ROUTE_SIGN_UP) {
                markQuizCompleted();
              }
              fullPageRedirect(path);
            }}
            redirectPath={afterBuilding}
          />
        </Route>
        <Route>
          <Quiz
            testid={testid}
            onQuizComplete={() => {
              if (WINDOW_LOCATION_REDIRECT_ROUTES.includes(afterQuiz)) {
                fullPageRedirect(afterQuiz);
                return;
              }
              history.push(afterQuiz);
            }}
          />
        </Route>
      </Switch>
    </>
  );
}

export function QuizContainerWithRouter() {
  return (
    <>
      <ErrorBoundary>
        <BrowserRouter>
          <QuizComponent testid="quiz" />
        </BrowserRouter>
      </ErrorBoundary>
    </>
  );
}
